import type { ReactNode } from "react";
import { useMemo } from "react";
import {
  GuestCheckoutSettingsProvider,
  useGuestCheckoutSettings,
  type DesignSystem,
} from "../context/GuestCheckoutSettingsContext";
import { THEMED_SURFACE_CLASS, useThemedSurface, type ThemedSurfaceOptions } from "../hooks/useThemedSurface";

interface ThemedSurfaceProps extends ThemedSurfaceOptions {
  /** Id of the surface root, which the flow's own stylesheet keys off. */
  readonly id: string;
  /** Flow-specific classes for the root, beside `gc-root` and the design marker. */
  readonly className?: string;
  /** Defaults to the design; a flow not yet migrated passes `legacy`. */
  readonly design?: DesignSystem;
  readonly children: ReactNode;
}

/**
 * The root of a surface that paints the design: resolves the colour scheme,
 * applies the venue's `--gc-*` overrides inline, marks the element for the
 * design stylesheets, and publishes the resolved settings.
 *
 * The settings extend whatever is already in context, so this works both as an
 * MFE's own root (where it inherits the defaults) and nested inside the full
 * app, where the app's own settings — locale, venues, the rest — are kept.
 */
export function ThemedSurface({
  id,
  className,
  design = "current",
  theme,
  customStyles,
  palette,
  children,
}: Readonly<ThemedSurfaceProps>) {
  const { widgetTheme, style, venueStyleKey, themeAttributes } = useThemedSurface({ theme, customStyles, palette });
  const inherited = useGuestCheckoutSettings();

  const settings = useMemo(
    () => ({ ...inherited, design, theme: widgetTheme, venueStyleKey }),
    [inherited, design, widgetTheme, venueStyleKey],
  );

  const classes = ["gc-root", THEMED_SURFACE_CLASS, className].filter(Boolean).join(" ");

  return (
    <GuestCheckoutSettingsProvider value={settings}>
      <div id={id} className={classes} style={style} {...themeAttributes}>
        {children}
      </div>
    </GuestCheckoutSettingsProvider>
  );
}
